import React, { useState, useEffect } from 'react';
import { useLang } from '../context/LanguageContext';
import { t } from '../data/translations';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

const langLabels = { pl: 'PL', en: 'EN', ua: 'UA' };
const langNext = { pl: 'EN', en: 'UA', ua: 'PL' };

const Header = () => {
  const { lang, toggleLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: t.nav.about[lang], href: '#about' },
    { label: t.nav.services[lang], href: '#services' },
    { label: t.nav.forCompanies[lang], href: '#for-companies' },
    { label: t.nav.contact[lang], href: '#contact' },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-[#E2E8F0]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a href="#" data-testid="logo-link" className="font-['Outfit'] font-black text-xl tracking-tight text-[#0F172A]">
          HIRIX
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="font-['Manrope'] text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors duration-200"
              data-testid={`nav-${link.href.replace('#', '')}`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Actions */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors border border-[#E2E8F0] rounded-none"
          >
            <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
            {langNext[lang]}
          </button>
          <Button
            data-testid="header-cta-btn"
            onClick={() => scrollTo('#contact')}
            className="bg-[#0F172A] text-white hover:bg-[#2563EB] rounded-none font-['Manrope'] text-sm font-semibold px-6 h-10 transition-colors duration-200"
          >
            {t.nav.cta[lang]}
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-[#0F172A]"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-white border-b border-[#E2E8F0] px-6 pb-6">
          <nav className="flex flex-col gap-4 pt-2">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="text-left font-['Manrope'] text-base font-medium text-[#475569] hover:text-[#0F172A]"
              >
                {link.label}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={toggleLang}
                data-testid="mobile-lang-toggle"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#475569] border border-[#E2E8F0]"
              >
                <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
                {langNext[lang]}
              </button>
              <Button
                data-testid="mobile-cta-btn"
                onClick={() => scrollTo('#contact')}
                className="bg-[#0F172A] text-white hover:bg-[#2563EB] rounded-none font-['Manrope'] text-sm font-semibold px-6 h-10"
              >
                {t.nav.cta[lang]}
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
